import React, { useState, useEffect } from 'react';
import { Clock, Play, Square, RefreshCw, CheckCircle, XCircle, AlertCircle, Activity, FileText, Eye, ShoppingCart } from 'lucide-react';

// Add custom CSS animations
const styles = `
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }

  @keyframes slide-in {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  .animate-shimmer {
    animation: shimmer 2s infinite;
  }

  .animate-slide-in {
    animation: slide-in 0.3s ease-out;
  }
`;

const OrderQuery = () => {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingInProgress, setProcessingInProgress] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [cronExpression, setCronExpression] = useState('0 4 * * *');
  const [logFilter, setLogFilter] = useState('');
  const [logPage, setLogPage] = useState(0);
  const [logPagination, setLogPagination] = useState(null);
  const [toast, setToast] = useState(null);
  const [errorModal, setErrorModal] = useState(null);
  const [loadingErrorDetails, setLoadingErrorDetails] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://ksp3plhq.com/api';

  // Detect user's timezone from browser
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Toast notification helper
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  // Placeholder - API not implemented yet
  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
      setConfig({
        currentStatus: { status: 'idle', operation: null },
        schedule: { isEnabled: false, cronExpression: '0 4 * * *', timezone: userTimezone }
      });
      setStats({ totalProcessed: 0, successRate: 0, avgExecutionTimeMs: 0, lastProcessTime: null });
    }, 1000);
  }, []);

  // Get status badge
  const getStatusBadge = (status) => {
    const badges = {
      idle: { bg: 'bg-slate-600', text: 'Idle', icon: <Clock className="w-4 h-4" /> },
      running: { bg: 'bg-blue-600', text: 'Running', icon: <Activity className="w-4 h-4 animate-pulse" /> },
      success: { bg: 'bg-green-600', text: 'Success', icon: <CheckCircle className="w-4 h-4" /> },
      error: { bg: 'bg-red-600', text: 'Error', icon: <XCircle className="w-4 h-4" /> },
      partial: { bg: 'bg-yellow-600', text: 'Partial', icon: <AlertCircle className="w-4 h-4" /> },
      stopped: { bg: 'bg-slate-600', text: 'Stopped', icon: <Square className="w-4 h-4" /> }
    };

    const badge = badges[status] || badges.idle;

    return (
      <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-white text-sm font-medium ${badge.bg}`}>
        {badge.icon}
        <span>{badge.text}</span>
      </span>
    );
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  // Format duration
  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="min-h-screen bg-slate-900">
        {/* Toast Notification */}
        {toast && (
          <div className="fixed top-4 right-4 z-50 animate-slide-in">
            <div className={`px-6 py-4 rounded-lg shadow-lg border-l-4 ${
              toast.type === 'success' ? 'bg-green-900 border-green-500' :
              toast.type === 'error' ? 'bg-red-900 border-red-500' :
              toast.type === 'warning' ? 'bg-yellow-900 border-yellow-500' :
              'bg-blue-900 border-blue-500'
            }`}>
              <div className="flex items-center space-x-3">
                {toast.type === 'success' && <CheckCircle className="w-5 h-5 text-green-400" />}
                {toast.type === 'error' && <XCircle className="w-5 h-5 text-red-400" />}
                {toast.type === 'warning' && <AlertCircle className="w-5 h-5 text-yellow-400" />}
                {toast.type === 'info' && <Activity className="w-5 h-5 text-blue-400" />}
                <p className="text-white font-medium">{toast.message}</p>
                <button
                  onClick={() => setToast(null)}
                  className="text-slate-400 hover:text-white"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <ShoppingCart className="w-10 h-10 text-cyan-400" />
              <h1 className="text-3xl font-bold text-slate-100">
                Order Query
              </h1>
            </div>
            <p className="text-lg text-slate-400">
              Automated CSV file processing from SAMBA share to MySQL database
            </p>
          </div>

          {/* Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Current Status</span>
                <Activity className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="mt-2">
                {config && getStatusBadge(config.currentStatus.status)}
              </div>
            </div>

            <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Total Processed</span>
                <RefreshCw className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="text-2xl font-bold text-slate-100">
                {stats?.totalProcessed || 0}
              </div>
              <p className="text-slate-500 text-xs mt-1">
                Success Rate: {stats?.successRate || 0}%
              </p>
            </div>

            <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">CSV Files Pending</span>
                <FileText className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="text-2xl font-bold text-slate-100">
                {files.length}
              </div>
              <p className="text-slate-500 text-xs mt-1">
                In rk_csv_imports directory
              </p>
            </div>

            <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Avg. Duration</span>
                <Clock className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="text-2xl font-bold text-slate-100">
                {formatDuration(stats?.avgExecutionTimeMs)}
              </div>
              <p className="text-slate-500 text-xs mt-1">
                Last run: {stats?.lastProcessTime ? formatTimestamp(stats.lastProcessTime) : 'Never'}
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-6">
            <div className="border-b border-slate-700">
              <nav className="-mb-px flex space-x-8">
                {['overview', 'files', 'schedule', 'logs'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                      activeTab === tab
                        ? 'border-cyan-500 text-cyan-400'
                        : 'border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-300'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* API Not Implemented Notice */}
              <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-6">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-yellow-300 mb-2">
                      API Not Implemented Yet
                    </h3>
                    <p className="text-yellow-200">
                      This is a placeholder interface. The backend API for Order Query processing has not been implemented yet.
                    </p>
                  </div>
                </div>
              </div>

              {/* Configuration Info */}
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-slate-100 mb-4">Configuration</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-400">CSV Source Directory</p>
                    <p className="text-slate-100 font-mono">/var/www/project-ksp-hq/project/rk_csv_imports</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Target Database</p>
                    <p className="text-slate-100 font-mono">Akamai MySQL</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Target Table</p>
                    <p className="text-slate-100 font-mono">Order_Query</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Poll Interval</p>
                    <p className="text-slate-100 font-mono">30 seconds</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'files' && (
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-slate-100 mb-4">CSV Files in Directory</h2>
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">API not implemented yet</p>
              </div>
            </div>
          )}

          {activeTab === 'schedule' && (
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-slate-100 mb-4">Schedule Configuration</h2>
              <div className="text-center py-12">
                <Clock className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">API not implemented yet</p>
              </div>
            </div>
          )}

          {activeTab === 'logs' && (
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-slate-100 mb-4">Execution Logs</h2>
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No logs found</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OrderQuery;
